/**
 * The canvas: the scene rendered by the graphic runtime itself (renderStatic, the same DOM builder, layout and
 * frame evaluation as graphic.mjs) at the authored resolution, scaled to fit on a checkerboard, with an HTML/SVG
 * overlay for selection, handles, marquee, snapping guides, safe areas, rulers and problem markers.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore, type PointerEvent as ReactPointerEvent } from 'react';
import type { Scene } from '@prism-graphics/scene-schema';
import { apply, createPlayer, renderStatic, type ElementGeometry, type Player, type Point, type StaticFrame } from '@prism-graphics/runtime/static';
import { previewSchedule } from '../model/preview.ts';
import { t } from '../i18n.ts';
import { modelGeo, moveElements, resizeElement, setTransform, type Rect } from '../model/commands.ts';
import type { Problem } from '../model/problems.ts';
import { centreCut, edgesX, edgesY, intersects, safeArea, snapRect, snapValue, unionRect, type SnapTargets } from '../model/snapping.ts';
import { findElement, isContainer, parentOf, pathTo, topmost } from '../model/tree.ts';
import { assetCache } from '../state/assets.ts';
import { sceneView, useEditor, type EditorStore } from '../state/store.ts';
import { geoFromFrame, hitTest, toLocalDelta } from './geo.ts';
import { Rulers } from './Rulers.tsx';

export interface Frames {
  test?: StaticFrame | undefined;
  defaults?: StaticFrame | undefined;
}

type Handle = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';
const HANDLES: Handle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
const HANDLE_POS: Record<Handle, [number, number]> = { nw: [0, 0], n: [0.5, 0], ne: [1, 0], e: [1, 0.5], se: [1, 1], s: [0.5, 1], sw: [0, 1], w: [0, 0.5] };

type Drag =
  | { kind: 'pending'; start: Point; screen: Point; ids: string[]; base: Scene; geometry: Map<string, ElementGeometry> }
  | { kind: 'move'; start: Point; ids: string[]; base: Scene; bounds: Rect; targets: SnapTargets }
  | { kind: 'resize'; handle: Handle; id: string; start: Point; base: Scene; g: ElementGeometry; targets: SnapTargets }
  | { kind: 'rotate'; id: string; base: Scene; center: Point; a0: number; r0: number }
  | { kind: 'marquee'; start: Point; current: Point; additive: boolean; before: string[] }
  | { kind: 'guide'; axis: 'x' | 'y'; index: number | null; value: number };

const PAD = 48;
const SNAP_PX = 6;

function dataDiffers(scene: Scene, testData: Record<string, unknown>): boolean {
  return scene.fields.some((f) => f.id in testData && testData[f.id] !== f.default);
}

export function Canvas({ store, problems, onFrames }: { store: EditorStore; problems: Problem[]; onFrames: (f: Frames) => void }) {
  const scene = useEditor(store, sceneView);
  const testData = useEditor(store, (s) => s.testData);
  const ui = useEditor(store, (s) => s.ui);
  const selection = useEditor(store, (s) => s.selection);
  const anim = useEditor(store, (s) => s.anim);
  const timelineOpen = ui.bottomTab === 'timeline';
  const assetsVersion = useSyncExternalStore(assetCache.subscribe, assetCache.getVersion);

  const viewportRef = useRef<HTMLDivElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const offscreenRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [view, setView] = useState({ width: 800, height: 450, scrollLeft: 0, scrollTop: 0 });
  const [frame, setFrame] = useState<StaticFrame>();
  const [renderError, setRenderError] = useState<string>();
  const [drag, setDrag] = useState<Drag>();
  const dragRef = useRef<Drag | undefined>(undefined);
  const [snapLines, setSnapLines] = useState<{ x: number[]; y: number[] }>({ x: [], y: [] });
  const [hover, setHover] = useState<string>();

  const W = scene.canvas.width;
  const H = scene.canvas.height;
  const fit = Math.max(0.05, Math.min((view.width - 2 * PAD) / W, (view.height - 2 * PAD) / H));
  const scale = ui.zoom === 'fit' ? fit : ui.zoom;
  const contentW = Math.max(view.width, W * scale + 2 * PAD);
  const contentH = Math.max(view.height, H * scale + 2 * PAD);
  const ox = (contentW - W * scale) / 2;
  const oy = (contentH - H * scale) / 2;

  // Geometry of the graphic in another layout (commands that run in every layout, e.g. grouping): an off-screen render.
  useEffect(() => {
    store.measure = (s: Scene) => {
      const host = measureRef.current;
      if (!host) return modelGeo(s);
      host.style.width = `${s.canvas.width}px`;
      host.style.height = `${s.canvas.height}px`;
      const shadow = host.shadowRoot ?? host.attachShadow({ mode: 'open' });
      return geoFromFrame(s, renderStatic(shadow, s, { families: assetCache.families, imageUrl: assetCache.imageUrl, state: ui.renderState, data: testData }));
    };
    return () => {
      store.measure = undefined;
    };
  }, [store, testData, ui.renderState]);

  // Viewport size.
  useLayoutEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setView((v) => ({ ...v, width: el.clientWidth, height: el.clientHeight })));
    ro.observe(el);
    setView((v) => ({ ...v, width: el.clientWidth, height: el.clientHeight }));
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    assetCache.ensure(scene.assets);
  }, [scene.assets]);

  // The timeline preview: the schedule that shows the open phase (the same frames goToTime renders).
  const preview = useMemo(() => (timelineOpen ? previewSchedule(scene, anim.target, testData) : undefined), [timelineOpen, scene, anim.target, testData]);
  const playerRef = useRef<{ player: Player; key: unknown } | undefined>(undefined);

  // Off-screen frames for the Problems list (the defaults, and the test data while the timeline shows another
  // frame), memoised: they depend on the scene and data only, so a test value, a scrub or a playhead move does not
  // re-render them.
  const offTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => {
    if (offTimer.current !== undefined) clearTimeout(offTimer.current);
  }, []);
  const offCache = useRef<{ defaults?: { key: unknown[]; frame: StaticFrame }; test?: { key: unknown[]; frame: StaticFrame } }>({});
  const sameKey = (a: unknown[] | undefined, b: unknown[]) => a !== undefined && a.length === b.length && a.every((x, i) => x === b[i]);

  // Render with the runtime (synchronously after every change), plus the defaults when test data differs.
  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host || anim.playing) return;
    const shadow = host.shadowRoot ?? host.attachShadow({ mode: 'open' });
    const opts = { families: assetCache.families, imageUrl: assetCache.imageUrl, state: ui.renderState };
    playerRef.current = undefined;
    try {
      const at = preview ? { schedule: preview.schedule, t: preview.offset + anim.time } : undefined;
      const f = renderStatic(shadow, scene, { ...opts, data: testData, ...(at ? { at } : {}) });
      setFrame(f);
      setRenderError(undefined);
      store.geo = geoFromFrame(scene, f);
      const off = offscreenRef.current;
      const offShadow = () => off!.shadowRoot ?? off!.attachShadow({ mode: 'open' });
      const cache = offCache.current;
      const needDefaults = Boolean(off) && dataDiffers(scene, testData);
      const needTest = Boolean(at && off);
      const dKey = [scene, ui.renderState, assetsVersion];
      const tKey = [scene, testData, ui.renderState, assetsVersion];
      const emit = () => onFrames({ test: needTest ? (cache.test?.frame ?? f) : f, defaults: needDefaults ? cache.defaults?.frame : undefined });
      if (offTimer.current !== undefined) clearTimeout(offTimer.current);
      offTimer.current = undefined;
      if ((!needDefaults || sameKey(cache.defaults?.key, dKey)) && (!needTest || sameKey(cache.test?.key, tKey))) emit();
      else {
        // The edit's frame is on screen first; the off-screen frames (for the Problems list) follow a moment later,
        // once edits pause.
        offTimer.current = setTimeout(() => {
          offTimer.current = undefined;
          try {
            if (needDefaults && !sameKey(cache.defaults?.key, dKey)) cache.defaults = { key: dKey, frame: renderStatic(offShadow(), scene, { ...opts, data: {} }) };
            if (needTest && !sameKey(cache.test?.key, tKey)) cache.test = { key: tKey, frame: renderStatic(offShadow(), scene, { ...opts, data: testData }) };
            emit();
          } catch (e) {
            setRenderError(e instanceof Error ? e.message : String(e));
          }
        }, OFFSCREEN_DELAY_MS);
      }
    } catch (e) {
      setRenderError(e instanceof Error ? e.message : String(e));
    }
  }, [scene, testData, ui.renderState, assetsVersion, store, onFrames, preview, anim.time, anim.playing]);

  // Playback: build the player once, then each frame only evaluates and writes (no layout, no rebuild).
  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host || !anim.playing || !preview) return;
    const shadow = host.shadowRoot ?? host.attachShadow({ mode: 'open' });
    const key = preview;
    if (playerRef.current?.key !== key) {
      try {
        playerRef.current = { player: createPlayer(shadow, scene, { families: assetCache.families, imageUrl: assetCache.imageUrl, data: testData, schedule: preview.schedule }), key };
      } catch (e) {
        setRenderError(e instanceof Error ? e.message : String(e));
        return;
      }
    }
    playerRef.current?.player.seek(preview.offset + anim.time);
  }, [anim.playing, anim.time, preview, scene, testData]);

  // ------------------------------------------------------------------------------------------------------------
  // Coordinates

  const toCanvas = useCallback(
    (clientX: number, clientY: number): Point => {
      const vp = viewportRef.current!.getBoundingClientRect();
      const sx = clientX - vp.left + viewportRef.current!.scrollLeft;
      const sy = clientY - vp.top + viewportRef.current!.scrollTop;
      return { x: (sx - ox) / scale, y: (sy - oy) / scale };
    },
    [ox, oy, scale],
  );
  const sx = (x: number) => ox + x * scale;
  const sy = (y: number) => oy + y * scale;

  const geometry = frame?.geometry;

  const snapTargets = useCallback(
    (exclude: readonly string[]): SnapTargets => {
      const xs = [0, W / 2, W];
      const ys = [0, H / 2, H];
      for (const m of ui.showSafe ? [ui.actionSafe, ui.graphicsSafe] : [ui.graphicsSafe]) {
        const r = safeArea(W, H, m);
        xs.push(r.x, r.x + r.width);
        ys.push(r.y, r.y + r.height);
      }
      if (ui.showGuides) {
        xs.push(...ui.guides.x);
        ys.push(...ui.guides.y);
      }
      if (geometry) {
        const excluded = (id: string) => exclude.some((e) => pathTo(scene, id).includes(e) || pathTo(scene, e).includes(id));
        for (const [id, g] of geometry) {
          if (!g.visible || excluded(id)) continue;
          xs.push(...edgesX(g.bounds));
          ys.push(...edgesY(g.bounds));
        }
      }
      return { x: xs, y: ys };
    },
    [W, H, ui.showSafe, ui.actionSafe, ui.graphicsSafe, ui.showGuides, ui.guides, geometry, scene],
  );

  // ------------------------------------------------------------------------------------------------------------
  // Pointer interaction

  const beginDrag = (d: Drag | undefined) => {
    dragRef.current = d;
    setDrag(d);
  };

  const pickTarget = (chain: string[], deepest: boolean): string => {
    if (deepest) return chain[chain.length - 1]!;
    const entered = new Set(selection.flatMap((id) => pathTo(scene, id).slice(0, -1)));
    let i = 0;
    chain.forEach((id, n) => {
      if (entered.has(id)) i = n + 1;
    });
    return chain[Math.min(i, chain.length - 1)]!;
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 || !geometry) return;
    const target = e.target as Element;
    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
    viewportRef.current?.focus({ preventScroll: true });
    const p = toCanvas(e.clientX, e.clientY);
    const handle = target.getAttribute('data-handle');
    const guide = target.getAttribute('data-guide');
    if (guide) {
      const [axis, index] = guide.split(':') as ['x' | 'y', string];
      beginDrag({ kind: 'guide', axis, index: Number(index), value: axis === 'x' ? p.x : p.y });
      return;
    }
    if (handle && selection.length === 1) {
      const id = selection[0]!;
      const g = geometry.get(id);
      const el = findElement(scene, id);
      if (!g || !el || el.locked) return;
      if (handle === 'rotate') {
        const center = apply(g.matrix, { x: g.box.width / 2, y: g.box.height / 2 });
        beginDrag({ kind: 'rotate', id, base: scene, center, a0: Math.atan2(p.y - center.y, p.x - center.x), r0: el.transform.rotation });
      } else beginDrag({ kind: 'resize', handle: handle as Handle, id, start: p, base: scene, g, targets: snapTargets([id]) });
      return;
    }
    const chain = hitTest(scene, geometry, p);
    if (!chain) {
      beginDrag({ kind: 'marquee', start: p, current: p, additive: e.shiftKey, before: e.shiftKey ? selection : [] });
      if (!e.shiftKey) store.select([]);
      return;
    }
    const id = pickTarget(chain, e.metaKey || e.ctrlKey);
    let ids: string[];
    if (e.shiftKey) ids = selection.includes(id) ? selection.filter((s) => s !== id) : [...selection, id];
    else ids = selection.includes(id) ? selection : [id];
    store.select(ids);
    const movable = topmost(scene, ids).filter((i) => !findElement(scene, i)?.locked);
    if (movable.length && !e.shiftKey) beginDrag({ kind: 'pending', start: p, screen: { x: e.clientX, y: e.clientY }, ids: movable, base: scene, geometry });
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    const p = toCanvas(e.clientX, e.clientY);
    if (!d) {
      if (geometry && e.buttons === 0) {
        const chain = hitTest(scene, geometry, p);
        setHover(chain ? pickTarget(chain, e.metaKey || e.ctrlKey) : undefined);
      }
      return;
    }
    const noSnap = !ui.snapping || e.metaKey || e.ctrlKey;
    const threshold = SNAP_PX / scale;
    switch (d.kind) {
      case 'pending': {
        if (Math.hypot(e.clientX - d.screen.x, e.clientY - d.screen.y) < 3) return;
        const rects = d.ids.map((id) => d.geometry.get(id)?.bounds).filter((r): r is Rect => r !== undefined);
        const bounds = unionRect(rects) ?? { x: d.start.x, y: d.start.y, width: 0, height: 0 };
        const next: Drag = { kind: 'move', start: d.start, ids: d.ids, base: d.base, bounds, targets: snapTargets(d.ids) };
        dragRef.current = next;
        setDrag(next);
        return onPointerMove(e);
      }
      case 'move': {
        let dx = p.x - d.start.x;
        let dy = p.y - d.start.y;
        if (e.shiftKey) {
          if (Math.abs(dx) > Math.abs(dy)) dy = 0;
          else dx = 0;
        }
        let lines = { x: [] as number[], y: [] as number[] };
        if (!noSnap) {
          const r = snapRect({ ...d.bounds, x: d.bounds.x + dx, y: d.bounds.y + dy }, d.targets, threshold, { x: !(e.shiftKey && dx === 0), y: !(e.shiftKey && dy === 0) });
          dx += r.dx;
          dy += r.dy;
          lines = r.lines;
        }
        setSnapLines(lines);
        dx = Math.round(dx * 100) / 100;
        dy = Math.round(dy * 100) / 100;
        store.preview(store.autoKeyed(d.base, moveElements(d.base, new Map(d.ids.map((id) => [id, { x: dx, y: dy }])))));
        return;
      }
      case 'resize': {
        const local = toLocalDelta(d.g.matrix, { x: p.x - d.start.x, y: p.y - d.start.y });
        const h = d.handle;
        const sxn = h.includes('e') ? 1 : h.includes('w') ? -1 : 0;
        const syn = h.includes('s') ? 1 : h.includes('n') ? -1 : 0;
        const w0 = d.g.box.width;
        const h0 = d.g.box.height;
        let w = w0 + sxn * local.x;
        let hh = h0 + syn * local.y;
        const el = findElement(d.base, d.id)!;
        const rotated = el.transform.rotation !== 0;
        const lines = { x: [] as number[], y: [] as number[] };
        if (!noSnap && !rotated) {
          const b = d.g.bounds;
          if (sxn === 1) {
            const s = snapValue(b.x + w, d.targets.x, threshold);
            w = s.value - b.x;
            if (s.line !== undefined) lines.x.push(s.line);
          } else if (sxn === -1) {
            const s = snapValue(b.x + b.width - w, d.targets.x, threshold);
            w = b.x + b.width - s.value;
            if (s.line !== undefined) lines.x.push(s.line);
          }
          if (syn === 1) {
            const s = snapValue(b.y + hh, d.targets.y, threshold);
            hh = s.value - b.y;
            if (s.line !== undefined) lines.y.push(s.line);
          } else if (syn === -1) {
            const s = snapValue(b.y + b.height - hh, d.targets.y, threshold);
            hh = b.y + b.height - s.value;
            if (s.line !== undefined) lines.y.push(s.line);
          }
        }
        if (e.shiftKey && sxn !== 0 && syn !== 0 && w0 > 0 && h0 > 0) {
          const k = Math.max(w / w0, hh / h0);
          w = w0 * k;
          hh = h0 * k;
        }
        w = Math.max(1, sxn === 0 ? w0 : w);
        hh = Math.max(1, syn === 0 ? h0 : hh);
        setSnapLines(lines);
        store.preview(store.autoKeyed(d.base, resizeElement(d.base, d.id, { width: w, height: hh, dLeft: sxn === -1 ? w0 - w : 0, dTop: syn === -1 ? h0 - hh : 0 })));
        return;
      }
      case 'rotate': {
        const a = Math.atan2(p.y - d.center.y, p.x - d.center.x);
        let r = d.r0 + ((a - d.a0) * 180) / Math.PI;
        r = e.shiftKey ? Math.round(r / 15) * 15 : Math.round(r * 10) / 10;
        r = ((((r + 180) % 360) + 360) % 360) - 180;
        store.preview(store.autoKeyed(d.base, setTransform(d.base, d.id, { rotation: r })));
        return;
      }
      case 'marquee': {
        const next = { ...d, current: p };
        dragRef.current = next;
        setDrag(next);
        const rect = { x: Math.min(d.start.x, p.x), y: Math.min(d.start.y, p.y), width: Math.abs(p.x - d.start.x), height: Math.abs(p.y - d.start.y) };
        const scopeParent = d.before.length ? parentOf(scene, d.before[0]!) : undefined;
        const candidates = scopeParent ? scopeParent.children : scene.elements;
        const hit = candidates.filter((el) => !el.locked && el.visible && geometry?.get(el.id)?.visible && intersects(rect, geometry.get(el.id)!.bounds)).map((el) => el.id);
        store.select(d.additive ? [...new Set([...d.before, ...hit])] : hit);
        return;
      }
      case 'guide': {
        const next = { ...d, value: Math.round(d.axis === 'x' ? p.x : p.y) };
        dragRef.current = next;
        setDrag(next);
        return;
      }
    }
  };

  const finishGuide = (d: Extract<Drag, { kind: 'guide' }>) => {
    const list = [...ui.guides[d.axis]];
    const max = d.axis === 'x' ? W : H;
    const inside = d.value >= 0 && d.value <= max;
    if (d.index === null) {
      if (inside) list.push(d.value);
    } else if (inside) list[d.index] = d.value;
    else list.splice(d.index, 1);
    store.setUi({ guides: { ...ui.guides, [d.axis]: list } });
  };

  const onPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    beginDrag(undefined);
    setSnapLines({ x: [], y: [] });
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    if (!d) return;
    if (d.kind === 'move' || d.kind === 'resize' || d.kind === 'rotate') store.commitFrom(d.base, store.scene);
    if (d.kind === 'guide') finishGuide(d);
  };

  const onDoubleClick = (e: React.MouseEvent) => {
    if (!geometry) return;
    const p = toCanvas(e.clientX, e.clientY);
    const chain = hitTest(scene, geometry, p);
    if (!chain) return;
    const current = selection.length === 1 ? chain.indexOf(selection[0]!) : -1;
    const next = chain[current + 1];
    if (current >= 0 && next) store.select([next]);
    else {
      const el = findElement(scene, chain[chain.length - 1]!);
      if (el?.type === 'text') {
        store.select([el.id]);
        store.requestTextFocus();
      }
    }
  };

  // Ruler drag: a new guide.
  const startGuide = (axis: 'x' | 'y', e: React.PointerEvent) => {
    e.preventDefault();
    const p = toCanvas(e.clientX, e.clientY);
    const d: Drag = { kind: 'guide', axis, index: null, value: Math.round(axis === 'x' ? p.x : p.y) };
    beginDrag(d);
    const move = (ev: PointerEvent) => {
      const q = toCanvas(ev.clientX, ev.clientY);
      const next: Drag = { kind: 'guide', axis, index: null, value: Math.round(axis === 'x' ? q.x : q.y) };
      dragRef.current = next;
      setDrag(next);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      const last = dragRef.current;
      beginDrag(undefined);
      if (last?.kind === 'guide') finishGuide(last);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  // ------------------------------------------------------------------------------------------------------------
  // Overlay

  const quadPoints = (g: ElementGeometry) => g.quad.map((q) => `${sx(q.x)},${sy(q.y)}`).join(' ');
  const problemIds = useMemo(() => {
    const m = new Map<string, Problem[]>();
    for (const p of problems) if (p.elementId && p.severity !== 'info') m.set(p.elementId, [...(m.get(p.elementId) ?? []), p]);
    return m;
  }, [problems]);

  const single = selection.length === 1 ? selection[0] : undefined;
  const singleG = single ? geometry?.get(single) : undefined;
  const singleEl = single ? findElement(scene, single) : undefined;
  const selBounds = unionRect(selection.map((id) => geometry?.get(id)?.bounds).filter((r): r is Rect => r !== undefined));
  const safeG = safeArea(W, H, ui.graphicsSafe);
  const safeA = safeArea(W, H, ui.actionSafe);
  const cut = centreCut(W, H);
  const bg = scene.canvas.background;
  const bgUrl = bg?.asset ? assetCache.imageUrl(bg.asset) : undefined;
  const rect = (r: Rect) => ({ x: sx(r.x), y: sy(r.y), width: r.width * scale, height: r.height * scale });

  return (
    <div className={`canvas-root${ui.showRulers ? ' with-rulers' : ''}`}>
      {ui.showRulers && (
        <Rulers width={view.width} height={view.height} scrollLeft={view.scrollLeft} scrollTop={view.scrollTop} ox={ox} oy={oy} scale={scale} selection={selBounds} onStartGuide={startGuide} />
      )}
      <div
        ref={viewportRef}
        className="canvas-viewport"
        tabIndex={0}
        role="application"
        aria-label={t('canvas.label', { width: W, height: H })}
        aria-describedby="canvas-help"
        data-testid="canvas-viewport"
        onScroll={(e) => setView((v) => ({ ...v, scrollLeft: e.currentTarget.scrollLeft, scrollTop: e.currentTarget.scrollTop }))}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={() => setHover(undefined)}
        onDoubleClick={onDoubleClick}
      >
        <span id="canvas-help" className="sr-only">
          {t('canvas.help')}
        </span>
        <div className="canvas-content" style={{ width: contentW, height: contentH }}>
          <div
            className={`canvas-stage${ui.showReference && bg?.color ? '' : ' checker'}`}
            data-testid="canvas-stage"
            style={{ left: ox, top: oy, width: W * scale, height: H * scale, ...(ui.showReference && bg?.color ? { background: bg.color } : {}) }}
          >
            {ui.showReference && bgUrl && <img className="canvas-reference" src={bgUrl} alt="" draggable={false} />}
          </div>
          <div ref={hostRef} className="canvas-host" data-testid="canvas-host" style={{ left: ox, top: oy, width: W, height: H, transform: `scale(${scale})` }} />
          <svg className="canvas-overlay" width={contentW} height={contentH} aria-hidden="true">
            <rect className="ov-frame" x={ox} y={oy} width={W * scale} height={H * scale} />
            {ui.showSafe && (
              <>
                <rect className="ov-safe-action" {...rect(safeA)} />
                <rect className="ov-safe-graphics" {...rect(safeG)} />
              </>
            )}
            {ui.showCut && cut && <rect className="ov-cut" {...rect(cut)} />}
            {ui.showGuides &&
              ui.guides.x.map((x, i) => (
                <g key={`gx${i}`}>
                  <line className="ov-guide" x1={sx(x)} x2={sx(x)} y1={0} y2={contentH} />
                  <line className="ov-guide-hit" data-guide={`x:${i}`} x1={sx(x)} x2={sx(x)} y1={0} y2={contentH} />
                </g>
              ))}
            {ui.showGuides &&
              ui.guides.y.map((y, i) => (
                <g key={`gy${i}`}>
                  <line className="ov-guide" y1={sy(y)} y2={sy(y)} x1={0} x2={contentW} />
                  <line className="ov-guide-hit" data-guide={`y:${i}`} y1={sy(y)} y2={sy(y)} x1={0} x2={contentW} />
                </g>
              ))}
            {drag?.kind === 'guide' && (drag.axis === 'x' ? <line className="ov-guide active" x1={sx(drag.value)} x2={sx(drag.value)} y1={0} y2={contentH} /> : <line className="ov-guide active" y1={sy(drag.value)} y2={sy(drag.value)} x1={0} x2={contentW} />)}
            {geometry &&
              [...problemIds.keys()].map((id) => {
                const g = geometry.get(id);
                if (!g?.visible) return null;
                const list = problemIds.get(id)!;
                const top = g.quad.reduce((a, q) => (q.y < a.y || (q.y === a.y && q.x > a.x) ? q : a), g.quad[0]);
                return (
                  <g key={`p-${id}`} className="ov-problem" data-testid="problem-marker" data-element-id={id}>
                    <polygon points={quadPoints(g)} />
                    <circle cx={sx(top.x)} cy={sy(top.y)} r={8} />
                    <text x={sx(top.x)} y={sy(top.y) + 4}>
                      !
                    </text>
                    <title>{list.map((p) => t(p.code, p.params)).join('\n')}</title>
                  </g>
                );
              })}
            {hover && !selection.includes(hover) && geometry?.get(hover) && !drag && <polygon className="ov-hover" points={quadPoints(geometry.get(hover)!)} />}
            {geometry &&
              !anim.playing &&
              selection.map((id) => {
                const g = geometry.get(id);
                return g ? <polygon key={`s-${id}`} className="ov-selection" points={quadPoints(g)} data-testid="selection-outline" /> : null;
              })}
            {selection.length > 1 && selBounds && <rect className="ov-selection-bounds" {...rect(selBounds)} />}
            {singleG && singleEl && !singleEl.locked && drag?.kind !== 'move' && !anim.playing && (
              <>
                {(() => {
                  const top = apply(singleG.matrix, { x: singleG.box.width / 2, y: 0 });
                  const c = apply(singleG.matrix, { x: singleG.box.width / 2, y: singleG.box.height / 2 });
                  const len = Math.hypot(top.x - c.x, top.y - c.y) || 1;
                  const ux = (top.x - c.x) / len;
                  const uy = (top.y - c.y) / len;
                  const rx = sx(top.x) + ux * 24;
                  const ry = sy(top.y) + uy * 24;
                  return (
                    <>
                      <line className="ov-rotate-stem" x1={sx(top.x)} y1={sy(top.y)} x2={rx} y2={ry} />
                      <circle className="ov-handle ov-rotate" data-handle="rotate" cx={rx} cy={ry} r={5} />
                    </>
                  );
                })()}
                {HANDLES.filter((h) => (singleEl.type === 'line' ? h === 'e' || h === 'w' : true)).map((h) => {
                  const [fx, fy] = HANDLE_POS[h];
                  const q = apply(singleG.matrix, { x: fx * singleG.box.width, y: fy * singleG.box.height });
                  return <rect key={h} className={`ov-handle h-${h}`} data-handle={h} x={sx(q.x) - 4} y={sy(q.y) - 4} width={8} height={8} />;
                })}
              </>
            )}
            {selBounds && (
              <text className="ov-size" x={sx(selBounds.x + selBounds.width / 2)} y={sy(selBounds.y + selBounds.height) + 16}>
                {`${Math.round(selBounds.width)} × ${Math.round(selBounds.height)}`}
              </text>
            )}
            {snapLines.x.map((x, i) => (
              <line key={`snx${i}`} className="ov-snap" x1={sx(x)} x2={sx(x)} y1={oy} y2={oy + H * scale} />
            ))}
            {snapLines.y.map((y, i) => (
              <line key={`sny${i}`} className="ov-snap" y1={sy(y)} y2={sy(y)} x1={ox} x2={ox + W * scale} />
            ))}
            {drag?.kind === 'marquee' && (
              <rect
                className="ov-marquee"
                {...rect({ x: Math.min(drag.start.x, drag.current.x), y: Math.min(drag.start.y, drag.current.y), width: Math.abs(drag.current.x - drag.start.x), height: Math.abs(drag.current.y - drag.start.y) })}
              />
            )}
          </svg>
        </div>
        {/* The same scene with the field defaults, measured off-screen for the Problems list. */}
        <div ref={offscreenRef} className="canvas-offscreen" style={{ width: W, height: H }} aria-hidden="true" />
        <div ref={measureRef} className="canvas-offscreen" aria-hidden="true" />
      </div>
      {renderError && <div className="canvas-error" role="alert">{t('canvas.renderError', { message: renderError })}</div>}
      <div className="canvas-zoom" aria-live="polite">
        {Math.round(scale * 100)}%
      </div>
    </div>
  );
}

/** How long the off-screen frames (Problems list) wait after the last edit or scrub. */
const OFFSCREEN_DELAY_MS = 40;

/** Whether a container's children are laid out by a stack (x/y become offsets). */
export function inStack(scene: Scene, id: string): boolean {
  const p = parentOf(scene, id);
  return Boolean(p && isContainer(p) && p.type === 'group' && p.layout.mode === 'stack');
}
